import axios from "axios";

import create from 'zustand'
import { persist } from "zustand/middleware";

import {
    applyAuthHeader,
    clearAuthSession,
    getAccessToken,
    getStoredUser,
    isAccessTokenExpired,
    logoutOnServer,
    persistAuthResponse,
    redirectToLogin,
    refreshAccessToken,
    refreshAccessTokenOnce,
    resetAuthSessionInvalidation,
    setStoredUser,
} from "../utils/authSession";

const urlCheck = process.env.REACT_APP_BASE_URL + 'php/models/login/check.php';
const urlEditPhoto = process.env.REACT_APP_BASE_URL + 'php/models/profile/change_photo.php';
const urlEditUser = process.env.REACT_APP_BASE_URL + 'php/models/profile/change_user.php';

const useAuthStore = create(
    persist(
        (set, get) => ({
            user: null,
            loading: false,

            error: false,
            errorText: "",
            setErrorText: (text) => {
                set({ error: true, errorText: text });
            },
            clearErrorText: () => {
                set({ error: false, errorText: "" });
            },

            setUser: (user) => {
                set({ user: user, loading: false, error: false, errorText: "" });
            },

            restoreSession: async () => {
                applyAuthHeader();

                const token = getAccessToken();
                const stored = getStoredUser();

                if (token && stored && !isAccessTokenExpired(token)) {
                    get().setUser(stored);
                    refreshAccessTokenOnce()
                        .then((user) => {
                            if (user) {
                                get().setUser(user);
                            }
                        })
                        .catch(() => { });
                    return true;
                }

                const user = await refreshAccessToken();

                if (user) {
                    get().setUser(user);
                    return true;
                }

                clearAuthSession();
                set({ user: null });

                return false;
            },

            fetchEditPhoto: async (params) => {

                let form = new FormData();

                for (let key in params) {

                    if (key === "photo") {
                        form.append("files[]", params[key]);
                    }
                    else {
                        form.append(key, params[key]);
                    }

                }

                const response = await axios.postForm(urlEditPhoto, form);

                let photo = "";

                if (response.data.params) {
                    photo = response.data.params;
                }

                set((state) => ({ user: { ...state.user, photo: photo }, loading: false, error: true, errorText: response.data.error_text }));

                const tmpUser = getStoredUser();
                if (tmpUser) {
                    tmpUser.photo = photo;
                    setStoredUser(tmpUser);
                }

            },
            fetchEditUser: async (params) => {

                let form = new FormData();
                window.global.buildFormData(form, params);

                const response = await axios.postForm(urlEditUser, form);

                set((state) => ({ user: { ...state.user, fio: params.fio, phone: params.phone, position: params.position }, loading: false, error: true, errorText: response.data.error_text }));

                const tmpUser = getStoredUser();
                if (tmpUser) {
                    tmpUser.fio = params.fio;
                    tmpUser.phone = params.phone;
                    tmpUser.position = params.position;
                    setStoredUser(tmpUser);
                }

            },

            login: async (params) => {

                set({ loading: true });

                const form = new FormData();
                form.append('login', params.login ?? '');
                form.append('password', params.password ?? '');
                if (params.remember) {
                    form.append('remember', '1');
                }

                const response = await axios.postForm(urlCheck, form, { withCredentials: true });

                if (response.data.error === 0 && response.data.access_token) {

                    window.localStorage.removeItem('login');
                    window.localStorage.removeItem('pwd');
                    window.localStorage.removeItem('remember');
                    window.localStorage.removeItem('user');

                    if (params.remember) {
                        window.localStorage.setItem('login', params.login);
                        window.localStorage.setItem('remember', '1');
                    }

                    resetAuthSessionInvalidation();
                    const user = persistAuthResponse(response.data);
                    get().setUser(user);

                } else {
                    set({ user: null, loading: false, error: true, errorText: response.data.error_text });
                }

                set({ loading: false });

            },
            logout: async (options = {}) => {
                const { redirect = true } = options;
                await logoutOnServer();
                set({ user: null });
                if (redirect) {
                    redirectToLogin();
                }
            }
        }),
        {
            name: "auth",
            getStorage: () => sessionStorage,
            partialize: (state) => ({ user: state.user }),
        }
    )
);

export default useAuthStore;
